import { useIntl } from "react-intl"
import { useCallback, useRef, useState } from "react"
import {
    Combobox,
    CustomTable,
    CustomTableRef,
    GetItemsProps,
} from "medere-ui-framework"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { Dropdown } from "primereact/dropdown"
import { ConfirmDialog } from "primereact/confirmdialog"
import "./HonorariumPreLiquidationView.scss"
import { cancelPreLiquidation, getPreLiquidations } from "../../servicesHooks/Honorarium/useHonorariumPreLiquidationService"
import {
    HonorariumPreLiquidationDTO,
    HonorariumPreLiquidationStatusId,
    PreLiquidationFilters,
} from "../../dto/HonorariumPreLiquidationDTO"
import { ComboDataDTO } from "../../dto/ComboDataDTO"
import { getAllCompanies } from "../../servicesHooks/Accounting/useAccountingService"
import { getProfessionals } from "../../servicesHooks/Budget/useBudgetService"
import { parseApiError } from "../../services/apiError"
import { formatMoney } from "../../utils/money"
import { confirmDelete } from "../../components/ConfirmDelete/confirmDelete"
import useNotify from "../../hooks/useNotify"
import { formatDate } from "../FinancerDistribution/financerDistributionFormat"
import FinancerDistributionPanel from "../FinancerDistribution/FinancerDistributionPanel"
import PreLiquidationDetail from "./PreLiquidationDetail"
import PreLiquidationStatusTag from "./PreLiquidationStatusTag"

type ViewMode = 'list' | 'create' | 'detail';

/** Estados del filtro: los mismos textos que el tag. Las canceladas no se listan. */
const STATUS_OPTIONS = [
    { label: 'Activa', value: 'ACTIVE' },
    { label: 'Completada', value: 'COMPLETED' },
];

/** Opción del combo de entidad facturadora (getAllCompanies). */
interface BillingEntityOption {
    company: number;
    description: string;
}

function toFilters(
    company: BillingEntityOption | null,
    professional: ComboDataDTO | null,
    status: string | null,
): PreLiquidationFilters {
    return {
        billingEntityId: company?.company,
        professionalId: professional?.id,
        status: status ?? undefined,
    };
}

/** Motivo por el que no se puede eliminar, o null si se puede (DS §3.8: lo no permitido se previene). */
function deleteBlockedReason(preLiq: HonorariumPreLiquidationDTO): string | null {
    if (preLiq.statusId !== HonorariumPreLiquidationStatusId.ACTIVE) return 'Ya está liquidada: no se puede eliminar';
    if (preLiq.liquidatedItemCount > 0) return 'Tiene ítems liquidados: no se puede eliminar';
    return null;
}

export default function HonorariumPreLiquidationView() {
    const intl = useIntl();
    const notify = useNotify();
    const tableRef = useRef<CustomTableRef>(null);

    const [mode, setMode] = useState<ViewMode>('list');
    const [detailPreLiqId, setDetailPreLiqId] = useState<number | null>(null);

    // ── Filtros del historial: se aplican con "Buscar" y se conservan al volver del detalle ──
    const [company, setCompany] = useState<BillingEntityOption | null>(null);
    const [professional, setProfessional] = useState<ComboDataDTO | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const appliedFiltersRef = useRef<PreLiquidationFilters>({});

    const getPreLiquidationsFiltered = useCallback(async (offset: number, pageSize: number) => {
        try {
            return await getPreLiquidations(offset, pageSize, appliedFiltersRef.current);
        } catch (error: unknown) {
            parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
            notify.error('No se pudieron cargar las pre-liquidaciones. Intentá de nuevo.');
            // CustomTable no captura el error: sin esto queda el spinner colgado
            return [];
        }
    }, [notify]);

    function handleSearch() {
        appliedFiltersRef.current = toFilters(company, professional, status);
        tableRef.current?.refreshTable?.();
    }

    function handleClearFilters() {
        setCompany(null);
        setProfessional(null);
        setStatus(null);
        appliedFiltersRef.current = {};
        tableRef.current?.refreshTable?.();
    }

    function handleViewDetail(preLiq: HonorariumPreLiquidationDTO) {
        setDetailPreLiqId(preLiq.honorariumPreLiquidation);
        setMode('detail');
    }

    // Al volver, la tabla se monta de nuevo y trae los montos actualizados.
    // Estable: el detalle lo usa como dependencia de su carga.
    const handleBackToList = useCallback(() => {
        setDetailPreLiqId(null);
        setMode('list');
    }, []);

    function handleDelete(preLiq: HonorariumPreLiquidationDTO) {
        confirmDelete({
            header: '¿Eliminar pre-liquidación?',
            message: `Se va a eliminar la pre-liquidación de ${preLiq.professionalName} (${preLiq.billingEntityName}, ${formatDate(preLiq.createdDatetime)}). `
                + 'Sus órdenes y recibos vuelven a quedar disponibles para pre-liquidar.',
            onAccept: async () => {
                try {
                    await cancelPreLiquidation(preLiq.honorariumPreLiquidation);
                    notify.success('Pre-liquidación eliminada');
                    tableRef.current?.refreshTable?.();
                } catch (error: unknown) {
                    // El backend manda un mensaje seguro para el usuario: el motivo de negocio o el error genérico
                    notify.error(parseApiError(error).message);
                }
            },
        });
    }

    function renderRowActions(preLiq: HonorariumPreLiquidationDTO) {
        const blockedReason = deleteBlockedReason(preLiq);
        return (
            <div className="flex justify-content-center">
                <Button
                    text
                    rounded
                    icon="pi pi-eye"
                    className="md-icon-btn"
                    aria-label={`Ver detalle de la pre-liquidación de ${preLiq.professionalName}`}
                    tooltip="Ver detalle"
                    tooltipOptions={{ position: 'left' }}
                    onClick={() => handleViewDetail(preLiq)}
                />
                <Button
                    text
                    rounded
                    icon="pi pi-trash"
                    className="md-icon-btn"
                    aria-label={`Eliminar la pre-liquidación de ${preLiq.professionalName}`}
                    tooltip={blockedReason ?? 'Eliminar'}
                    tooltipOptions={{ position: 'left', showOnDisabled: true }}
                    disabled={blockedReason !== null}
                    onClick={() => handleDelete(preLiq)}
                />
            </div>
        );
    }

    if (mode === 'create') {
        return (
            <div className="honorarium-preliquidation px-4 flex flex-column viewContainer">
                <ConfirmDialog />
                <div>
                    <Button
                        text
                        icon="pi pi-angle-double-left"
                        label={intl.formatMessage({ id: 'Back', defaultMessage: 'Volver' })}
                        className="md-back-link"
                        onClick={() => setMode('list')}
                    />
                </div>
                <h1 className="md-page-title honorarium-preliquidation__create-title">
                    {intl.formatMessage({ id: 'NewPreLiquidation', defaultMessage: 'Nueva pre-liquidación' })}
                </h1>
                {/* Al volver al listado, la tabla se monta de nuevo y trae la pre-liquidación creada */}
                <FinancerDistributionPanel onCreated={() => setMode('list')} />
            </div>
        );
    }

    if (mode === 'detail' && detailPreLiqId !== null) {
        return (
            <div className="honorarium-preliquidation px-4 flex flex-column viewContainer">
                <ConfirmDialog />
                <PreLiquidationDetail preLiqId={detailPreLiqId} onBack={handleBackToList} />
            </div>
        );
    }

    return (
        <div className="honorarium-preliquidation px-4 flex flex-column viewContainer">
            <ConfirmDialog />

            <div className="md-header-row">
                <h1 className="md-page-title">
                    {intl.formatMessage({ id: 'HonorariumPreLiquidation', defaultMessage: 'Pre-liquidación de honorarios' })}
                </h1>
                <Button
                    label="Crear"
                    icon="pi pi-plus-circle"
                    className="md-primary"
                    onClick={() => setMode('create')}
                />
            </div>

            <div className="md-filters">
                <Combobox
                    label={intl.formatMessage({ id: 'BillingEntity', defaultMessage: 'Entidad facturadora' })}
                    getItems={(p: GetItemsProps) => getAllCompanies(p)}
                    optionLabel="description"
                    value={company}
                    setValue={setCompany}
                    placeholder="Todas"
                    style={{ width: '220px' }}
                />
                <Combobox
                    label={intl.formatMessage({ id: 'Professional', defaultMessage: 'Profesional' })}
                    getItems={(p: GetItemsProps) => getProfessionals(p)}
                    optionLabel="name"
                    value={professional}
                    setValue={setProfessional}
                    placeholder="Todos"
                    style={{ width: '220px' }}
                />
                {/* Mismo contenedor y label que Combobox, para que la barra quede alineada */}
                <div className="inputContainer flexible--column" style={{ width: '180px' }}>
                    <label htmlFor="preliq-status-filter" className="label inputLabel">
                        {intl.formatMessage({ id: 'Status', defaultMessage: 'Estado' })}
                    </label>
                    <Dropdown
                        inputId="preliq-status-filter"
                        value={status}
                        options={STATUS_OPTIONS}
                        onChange={(e) => setStatus(e.value ?? null)}
                        placeholder="Todos"
                        showClear
                    />
                </div>
                <Button
                    outlined
                    className="md-ghost md-filter-action"
                    icon="pi pi-search"
                    label={intl.formatMessage({ id: 'Search', defaultMessage: 'Buscar' })}
                    onClick={handleSearch}
                />
                <Button
                    text
                    rounded
                    icon="pi pi-eraser"
                    className="md-icon-btn md-filter-action"
                    aria-label="Limpiar filtros"
                    tooltip="Limpiar filtros"
                    tooltipOptions={{ position: 'top' }}
                    onClick={handleClearFilters}
                />
            </div>

            <CustomTable
                idTable="tablePreLiquidations"
                ref={tableRef}
                getItems={getPreLiquidationsFiltered}
                className="crudTable"
                filterRow={false}
                hideFiltersButton
                notFoundText="No hay resultados para estos filtros."
            >
                <Column field="professionalName" header={intl.formatMessage({ id: 'Professional', defaultMessage: 'Profesional' })} />
                <Column field="billingEntityName" header={intl.formatMessage({ id: 'BillingEntity', defaultMessage: 'Entidad facturadora' })} />
                <Column
                    field="createdDatetime"
                    header="Fecha"
                    body={(row: HonorariumPreLiquidationDTO) => formatDate(row.createdDatetime)}
                    style={{ width: '120px' }}
                />
                <Column
                    field="status"
                    header={intl.formatMessage({ id: 'Status', defaultMessage: 'Estado' })}
                    body={(row: HonorariumPreLiquidationDTO) => <PreLiquidationStatusTag statusId={row.statusId} status={row.status} />}
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '130px' }}
                />
                <Column
                    field="pendingItemCount"
                    header="Ítems pendientes"
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '110px' }}
                />
                <Column
                    field="liquidatedItemCount"
                    header="Ítems liquidados"
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '110px' }}
                />
                <Column
                    field="totalProfessionalAmount"
                    header="Honorario profesional"
                    body={(row: HonorariumPreLiquidationDTO) => formatMoney(row.totalProfessionalAmount)}
                    className="text-right honorarium-preliquidation__amount"
                    headerClassName="col-right"
                    style={{ width: '170px' }}
                />
                <Column
                    header=""
                    body={renderRowActions}
                    style={{ width: '96px' }}
                />
            </CustomTable>
        </div>
    );
}
